import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { phoneVariations } from './phone';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Configure retry options
const RETRY_COUNT = 1; // Reduced for Instagram browser
const RETRY_DELAY = 300; // 0.3 seconds
const REQUEST_TIMEOUT = 60000; // 60 seconds (increased for Edge Functions that take longer)

// Add retry wrapper with exponential backoff
async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries = RETRY_COUNT,
  initialDelay = RETRY_DELAY
): Promise<T> {
  let lastError: any;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if it's an abort error
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      
      // Don't retry on authentication errors
      if ((error as any)?.message?.includes('JWT')) {
        throw error;
      }

      // Only retry on network errors or 5xx server errors
      const shouldRetry = error instanceof TypeError || 
                         ((error as any)?.status >= 500 && (error as any)?.status < 600);
      
      if (!shouldRetry || i === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff with jitter
      const delay = initialDelay * Math.pow(2, i) * (0.5 + Math.random() * 0.5);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'video-feedback-webapp',
      'X-Timezone': 'Europe/Berlin'
    }
  },
  // Add request timeout and retry logic with proper CORS handling
  // @ts-ignore - fetch option exists in runtime but not in SupabaseClientOptions types
  fetch: (url: string, options: any = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const headers = new Headers(options.headers || {});
    if (!headers.has('apikey')) {
      headers.set('apikey', supabaseAnonKey);
    }
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${supabaseAnonKey}`);
    }
    headers.set('Cache-Control', 'no-cache');

    const fetchOptions: RequestInit = {
      ...options,
      signal: controller.signal,
      mode: 'cors' as RequestMode,
      credentials: 'same-origin' as RequestCredentials,
      headers
    };

    return withRetry(
      () => fetch(url, fetchOptions)
        .then(async response => {
          if (!response.ok) {
            // Handle authentication errors
            if (response.status === 400 || response.status === 401) {
              try {
                const responseText = await response.text();
                const errorData = JSON.parse(responseText);
                
                // Check for refresh token errors or JWT expiration
                if (errorData.code === 'refresh_token_not_found' || 
                    errorData.message?.includes('JWT expired') ||
                    errorData.message?.includes('Invalid Refresh Token')) {
                  
                  // Force sign out and redirect to login
                  try {
                    await supabase.auth.signOut();
                  } catch (signOutError) {
                    console.error('Failed to sign out:', signOutError);
                    // Fallback: clear local storage
                    localStorage.clear();
                  }
                  
                  // Redirect to login page
                  window.location.href = '/login';
                  return response;
                }
                
                // Re-create response with the text we already read
                const newResponse = new Response(responseText, {
                  status: response.status,
                  statusText: response.statusText,
                  headers: response.headers
                });
                throw new Error(`HTTP error! status: ${newResponse.status}`);
              } catch (parseError) {
                // If we can't parse the response, just throw the original error
                throw new Error(`HTTP error! status: ${response.status}`);
              }
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response;
        })
        .finally(() => {
          clearTimeout(timeoutId);
        }),
      RETRY_COUNT,
      RETRY_DELAY
    );
  }
});

// Add health check function with retry and better error handling
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from('artists').select('id').limit(1).single();
    if (error) {
      if (error.code === 'PGRST116') {
        // No data found is still a successful connection
        return true;
      }
      console.error('Supabase connection check failed:', error.message);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to connect to Supabase:', error);
    return false;
  }
}

// Add connection state management with improved error handling
let isConnected = true;
let connectionCheckInterval: number | null = null;

export function startConnectionMonitoring(onConnectionChange?: (connected: boolean) => void) {
  if (connectionCheckInterval) return;

  const checkConnection = async () => {
    try {
      const connected = await checkSupabaseConnection();
      if (connected !== isConnected) {
        isConnected = connected;
        onConnectionChange?.(connected);
      }
    } catch (error) {
      console.error('Connection monitoring error:', error);
      if (isConnected) {
        isConnected = false;
        onConnectionChange?.(false);
      }
    }
  };

  connectionCheckInterval = window.setInterval(checkConnection, 30000) as unknown as number;

  // Initial check
  checkConnection();

  return () => {
    if (connectionCheckInterval) {
      clearInterval(connectionCheckInterval);
      connectionCheckInterval = null;
    }
  };
}

export function getConnectionState(): boolean {
  return isConnected;
}

// ---------------------------------------------------------------------------
// Artist-scoped Supabase client (token-based RLS)
// ---------------------------------------------------------------------------
// For artists in the rls_protected_artists table, the frontend mints a
// signed JWT via the mint-artist-token edge function after WhatsApp phone
// verification. This client sends that JWT as the Bearer token so RLS
// policies scope queries to the artist's own rows.

export type ArtistToken = {
  token: string;
  refresh_token?: string;
  expires_at: string;
  phone: string;
  display_name?: string | null;
  avatar_url?: string | null;
  user_id?: string | null;
};

const ARTIST_TOKEN_KEY = 'artist_token';

/** Persist the artist token in localStorage. JWT expires in 7 days,
 *  refresh token keeps session alive for 30 days via auto-refresh. */
export function storeArtistToken(t: ArtistToken): void {
  localStorage.setItem(ARTIST_TOKEN_KEY, JSON.stringify(t));
}

/** Read the stored artist token. Returns null if missing or expired. */
export function readArtistToken(): ArtistToken | null {
  const raw = localStorage.getItem(ARTIST_TOKEN_KEY);
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as ArtistToken;
    if (new Date(t.expires_at).getTime() < Date.now()) {
      localStorage.removeItem(ARTIST_TOKEN_KEY);
      return null;
    }
    // For tokens minted before user_id was added, extract it from the JWT
    if (!t.user_id && t.token) {
      try {
        const payload = JSON.parse(atob(t.token.split('.')[1]));
        if (payload.sub) {
          t.user_id = payload.sub;
        }
      } catch {
        // ignore JWT parse errors
      }
    }
    return t;
  } catch {
    return null;
  }
}

/** Remove the stored artist token (e.g. on sign-out). */
export function clearArtistToken(): void {
  localStorage.removeItem(ARTIST_TOKEN_KEY);
}

/**
 * Build a Supabase client scoped to an artist via their JWT.
 * Use this on artist routes (/artist/:id/*) for protected artists.
 */
export function createArtistClient(token: string) {
  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storage: { getItem: () => null, setItem: () => {}, removeItem: () => {} },
    },
    global: {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Client-Info': 'video-feedback-webapp-artist',
      },
    },
  });
}

/**
 * Mint an artist token via the mint-artist-token edge function.
 * Called by ArtistAuthGuard after phone verification succeeds.
 * One token per phone — works for all artists whose WhatsApp group the phone is in.
 */
export async function mintArtistToken(artistId: string, phone: string): Promise<ArtistToken> {
  const res = await fetch(`${supabaseUrl}/functions/v1/mint-artist-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${supabaseAnonKey}`,
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify({ artist_id: artistId, phone }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Failed to mint artist token (HTTP ${res.status})`);
  }
  const data = await res.json() as {
    token: string;
    expires_at: string;
    phone: string;
    display_name?: string | null;
    avatar_url?: string | null;
    user_id?: string | null;
  };
  const t: ArtistToken = {
    token: data.token,
    refresh_token: (data as any).refresh_token,
    expires_at: data.expires_at,
    phone: data.phone,
    display_name: data.display_name ?? null,
    avatar_url: data.avatar_url ?? null,
    user_id: (data as any).user_id ?? null,
  };
  storeArtistToken(t);
  return t;
}

/**
 * Update the artist's display name using their JWT.
 * Uses the Supabase auth REST API to update user_metadata.
 */
export async function updateArtistDisplayName(displayName: string): Promise<void> {
  const token = readArtistToken();
  if (!token) throw new Error('No artist token found');

  const res = await fetch(`${supabaseUrl}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${token.token}`,
    },
    body: JSON.stringify({
      data: {
        phone: token.phone,
        type: 'artist',
        display_name: displayName,
        avatar_url: token.avatar_url ?? null,
      },
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Failed to update display name (HTTP ${res.status})`);
  }

  // Update stored token with new display_name
  const updated: ArtistToken = { ...token, display_name: displayName };
  storeArtistToken(updated);
}

/**
 * Upload an avatar image to the avatars storage bucket using the artist's JWT.
 * Returns the public URL of the uploaded image.
 */
export async function uploadArtistAvatar(file: File): Promise<string> {
  const token = readArtistToken();
  if (!token) throw new Error('No artist token found');

  // Generate a unique filename based on phone + timestamp
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const filename = `${token.phone.replace(/\D/g, '')}_${Date.now()}.${ext}`;

  // Upload using the artist-scoped client
  const artistClient = createArtistClient(token.token);
  const { error: uploadError } = await artistClient.storage
    .from('avatars')
    .upload(filename, file, { contentType: file.type, upsert: true });

  if (uploadError) throw uploadError;

  // Get the public URL
  const { data: urlData } = artistClient.storage
    .from('avatars')
    .getPublicUrl(filename);

  const avatarUrl = urlData.publicUrl;

  // Update user_metadata with the avatar URL
  const res = await fetch(`${supabaseUrl}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${token.token}`,
    },
    body: JSON.stringify({
      data: {
        phone: token.phone,
        type: 'artist',
        display_name: token.display_name ?? null,
        avatar_url: avatarUrl,
      },
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Failed to update avatar (HTTP ${res.status})`);
  }

  // Update stored token
  const updated: ArtistToken = { ...token, avatar_url: avatarUrl };
  storeArtistToken(updated);

  return avatarUrl;
}

/**
 * Fetch the artist's WhatsApp profile (name + avatar) and update user_metadata.
 * Calls the fetch-whatsapp-profile edge function which verifies group membership
 * and fetches the profile via Whapi. Overwrites existing display_name and avatar_url.
 * Returns the fetched { display_name, avatar_url } or null values if not available.
 */
export async function fetchProfileFromWhatsApp(artistId: string): Promise<{ display_name: string | null; avatar_url: string | null }> {
  const token = readArtistToken();
  if (!token) throw new Error('No artist token found');

  const res = await fetch(`${supabaseUrl}/functions/v1/fetch-whatsapp-profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${token.token}`,
    },
    body: JSON.stringify({
      artist_id: artistId,
      phone: token.phone,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Failed to fetch WhatsApp profile (HTTP ${res.status})`);
  }

  const data = await res.json() as { display_name: string | null; avatar_url: string | null };

  // Update stored token with fetched values
  const updated: ArtistToken = {
    ...token,
    display_name: data.display_name ?? token.display_name,
    avatar_url: data.avatar_url ?? token.avatar_url,
  };
  storeArtistToken(updated);

  return data;
}

/**
 * Refresh the artist token using the stored refresh_token.
 * Returns a new ArtistToken or null if refresh fails.
 */
export async function refreshArtistToken(): Promise<ArtistToken | null> {
  const raw = localStorage.getItem(ARTIST_TOKEN_KEY);
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as ArtistToken;
    if (!t.refresh_token) return null;

    const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({ refresh_token: t.refresh_token }),
    });
    if (!res.ok) {
      console.error('[refreshArtistToken] Refresh failed:', res.status);
      localStorage.removeItem(ARTIST_TOKEN_KEY);
      return null;
    }
    const data = await res.json();
    const newToken: ArtistToken = {
      token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: new Date((data.expires_at ?? 0) * 1000).toISOString(),
      phone: t.phone,
    };
    storeArtistToken(newToken);
    console.log('[refreshArtistToken] Token refreshed, expires:', newToken.expires_at);
    return newToken;
  } catch (err) {
    console.error('[refreshArtistToken] Error:', err);
    localStorage.removeItem(ARTIST_TOKEN_KEY);
    return null;
  }
}

/**
 * Read the stored artist token. If expired but a refresh_token exists,
 * attempt a refresh. Returns null if missing/expired/unrefreshable.
 */
export async function readArtistTokenAsync(): Promise<ArtistToken | null> {
  const raw = localStorage.getItem(ARTIST_TOKEN_KEY);
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as ArtistToken;
    if (new Date(t.expires_at).getTime() < Date.now()) {
      // Expired — try refresh
      const refreshed = await refreshArtistToken();
      return refreshed;
    }
    return t;
  } catch {
    return null;
  }
}

/**
 * Check whether an artist is in the rls_protected_artists table.
 * If so, the frontend must use createArtistClient instead of the global supabase.
 */
export async function isArtistProtected(artistId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('rls_protected_artists')
    .select('artist_id')
    .eq('artist_id', artistId)
    .maybeSingle();
  if (error) {
    console.error('isArtistProtected check failed:', error.message);
    return false;
  }
  return !!data;
}

/**
 * Verify that the phone number stored with the token is still a member
 * of the artist's WhatsApp group. Called on page load to ensure the
 * user hasn't left the group since the token was minted.
 *
 * Uses the global supabase client (anon key) — this only reads the
 * artist_whatsapp_group_members table which is not RLS-protected.
 */
export async function verifyArtistMembership(artistId: string, phone: string): Promise<boolean> {
  if (!phone) return false;

  // Generate all format variations for matching against stored member_phone
  // (canonical digits, +CC, 00CC, 0-national, bare national — all countries)
  const searchPhones = phoneVariations(phone);

  // Get the artist's whatsapp_group_id
  const { data: artist, error: artistError } = await supabase
    .from('artists')
    .select('whatsapp_group_id')
    .eq('id', artistId)
    .maybeSingle();

  if (artistError || !artist?.whatsapp_group_id) return false;

  // Check if any phone variation is in the group
  const { data: member, error: memberError } = await supabase
    .from('artist_whatsapp_group_members')
    .select('id')
    .eq('whatsapp_group_id', artist.whatsapp_group_id)
    .in('member_phone', searchPhones)
    .limit(1)
    .maybeSingle();

  if (memberError || !member) return false;

  return true;
}